/**
 * The one place that talks to the server.
 *
 * Requests are same-origin — in development Vite proxies `/api` to the hub —
 * which settles two things at once. The session cookie rides along without any
 * cross-origin arrangement, and the browser attaches Origin and Referer of its
 * own accord, which is what the server's CSRF check looks for. Calling the hub
 * cross-origin instead would break both.
 */

import type { WorkingPeriod } from "@wmf/shared/workingHours";

export const AWARD_EVIDENCE_EVENT = "wmf:award-evidence-changed";
function awardEvidenceChanged(path: string): void {
  const metric = /^\/practice-metrics\/family\/([^/]+)\//.exec(path);
  if(metric) path=`/me/students/${metric[1]}/practice`;
  const noteView = /^\/engagement\/family\/students\/([^/]+)\/notes\//.exec(path);
  const evidencePath = noteView ? `/me/students/${noteView[1]}/note-view` : path;
  if (typeof window !== "undefined" && /^\/me\/students\/[^/]+\//.test(evidencePath) && !/\/awards(?:\/|$)/.test(evidencePath)) {
    window.dispatchEvent(new CustomEvent(AWARD_EVIDENCE_EVENT, { detail: { path: evidencePath } }));
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * A machine-readable reason, where the server offers one. Present when the
     * page has something to do about it — "unconfirmed" is answered with a
     * resend rather than by re-reading the message.
     */
    readonly code?: string,
    /**
     * The whole response body, where the server sent one worth reading.
     *
     * A refused save is the case this exists for: the server answers a stale
     * write with what is actually stored, and a page that could only read the
     * message would have to ask for it again to show the person what they
     * would have overwritten.
     */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function unwrap<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      body && typeof body === "object" && "error" in body && typeof body.error === "string"
        ? body.error
        : `Request failed (${response.status})`;
    const code =
      body && typeof body === "object" && "code" in body && typeof body.code === "string"
        ? body.code
        : undefined;
    throw new ApiError(response.status, message, code, body);
  }
  return body as T;
}

export async function get<T>(
  path: string,
  params?: Record<string, string | readonly string[] | undefined>,
): Promise<T> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (typeof value === "string") {
      if (value !== "") query.set(key, value);
    } else if (value) {
      for (const item of value) {
        if (item !== "") query.append(key, item);
      }
    }
  }
  const suffix = query.toString() ? `?${query}` : "";
  return unwrap<T>(await fetch(`/api${path}${suffix}`, { headers: { Accept: "application/json" } }));
}

/**
 * Send a file.
 *
 * No `Content-Type` of our own: the browser has to set it, because only it
 * knows the multipart boundary it is about to generate.
 */
export async function upload<T>(
  path: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  const result = await unwrap<T>(
    await fetch(`/api${path}`, { method: "POST", headers: { Accept: "application/json" }, body: form }),
  );
  awardEvidenceChanged(path);
  return result;
}

/**
 * `headers` exists for one thing: `Idempotency-Key`.
 *
 * A booking has to carry the same key across a retry, and the browser is the
 * only place that knows its second attempt is the same attempt — the server
 * cannot tell a retry from a second booking, which is the whole problem.
 */
export async function send<T>(
  method: string,
  path: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  const result = await unwrap<T>(
    await fetch(`/api${path}`, {
      method,
      headers: { "Content-Type": "application/json", Accept: "application/json", ...headers },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
  awardEvidenceChanged(path);
  return result;
}

/**
 * Ask for a reset link.
 *
 * Resolves whether or not there is such an account — the server answers the
 * same way either way, and the caller must not be able to tell them apart.
 */
export async function requestPasswordReset(identifier: string): Promise<void> {
  await send("POST", "/auth/forgot-password", { identifier });
}

/** Spend a reset link. Throws with the server's message when it is unusable. */
export async function resetPassword(token: string, password: string): Promise<void> {
  await send("POST", "/auth/reset-password", { token, password });
}

/**
 * Register.
 *
 * Resolves whether the address was new, already registered but unconfirmed, or
 * already has an account — the server answers identically, and the caller must
 * not be able to tell them apart. Nothing is signed in; a link goes out first.
 */
export async function register(email: string): Promise<void> {
  await send("POST", "/auth/register", { email });
}

/**
 * Spend a confirmation link.
 *
 * Says whether it found records for the address, and whether it moved the
 * account onto a new one — the same link serves registering and changing an
 * address, and the page has different things to say about each.
 *
 * Finishing a registration needs `password`: whoever holds the link chooses
 * the account's password. Sent without one for an unconfirmed account, the
 * server answers `password_required` and leaves the link usable.
 */
export async function confirmEmail(
  token: string,
  password?: string,
): Promise<{ linked: boolean; moved: boolean; purchaseClaimPending?: boolean }> {
  return send<{ ok: true; linked: boolean; moved: boolean; purchaseClaimPending?: boolean }>("POST", "/auth/confirm-email", {
    token,
    ...(password !== undefined ? { password } : {}),
  });
}

/**
 * Ask to sign in with a different address. Staff only.
 *
 * Nothing changes until the link sent to the new address is used. The old
 * address is told, so a change nobody meant cannot happen quietly.
 */
export async function changeMyEmail(email: string, password: string): Promise<{ sentTo: string }> {
  return send<{ ok: true; sentTo: string }>("POST", "/me/email", { email, password });
}

/**
 * Change the password on this account. Takes effect at once.
 *
 * The current one is required: a session is a weaker thing than a password,
 * and without it a tab left open would be enough to lock somebody out of their
 * own account. Answers with how many other sessions were ended, which is worth
 * saying back — it is the reassuring half of "signed out everywhere else".
 */
export async function changeMyPassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ otherSessionsEnded: number }> {
  return send<{ ok: true; otherSessionsEnded: number }>("POST", "/me/password", {
    currentPassword,
    newPassword,
  });
}

/** Another confirmation link. Resolves the same way whatever it finds. */
export async function resendConfirmation(email: string): Promise<void> {
  await send("POST", "/auth/resend-confirmation", { email });
}

/**
 * Read an invitation without spending it.
 *
 * A POST, not a GET: the token would otherwise be written into every access
 * log between here and the server, and this one grants staff access. Throws
 * with the server's message when the invitation is expired, withdrawn or spent.
 */
export async function readInvitation(token: string): Promise<Invitation> {
  return send<Invitation>("POST", "/auth/invitation", { token });
}

/**
 * Spend an invitation and become a tutor.
 *
 * `password` is omitted when the invitation went to an address that already has
 * a working account — `needsPassword` on the invitation says which, and sending
 * one anyway would not replace the password they already use.
 */
export async function acceptInvitation(
  token: string,
  displayName: string,
  password?: string,
  dbsCheckConsent?: boolean,
): Promise<{
  email: string;
  passwordSet: boolean;
  bookingSetupNeedsReview?: boolean;
}> {
  return send("POST", "/auth/accept-invitation", {
    token,
    displayName,
    password,
    dbsCheckConsent,
  });
}

export interface HubInvitationLookup {
  email: string;
  organisationName: string;
  needsPassword: boolean;
  purchaseClaimPending?: boolean;
}

export function readHubInvitation(token: string): Promise<HubInvitationLookup> {
  return send("POST", "/auth/hub-invitation", { token });
}

export function acceptHubInvitation(
  token: string,
  password?: string,
): Promise<{ ok: true; email: string; passwordSet: boolean; linked: boolean; purchaseClaimPending?: boolean }> {
  return send("POST", "/auth/accept-hub-invitation", { token, password });
}

/**
 * Answer, or change the answer to, "may we check you on the Update Service".
 *
 * Its own call rather than part of `saveMyDbs`, because it is a different
 * question with a different answer: somebody who has not yet found their
 * certificate can still say yes, and the DBS form requires every field it has.
 */
export async function setMyDbsCheckConsent(
  consent: boolean,
): Promise<{ dbsCheckConsent: boolean | null; dbsCheckConsentAt: string | null }> {
  return send("PUT", "/me/dbs-check-consent", { consent });
}

/** A tutor's own DBS. Nobody else's, and no other tutor sees theirs. */
export async function getMyDbs(): Promise<{
  dbs: DbsView | null;
  consent: DbsCheckConsent;
  today: string;
}> {
  return get("/me/dbs");
}

export async function saveMyDbs(input: DbsInput): Promise<{ dbs: DbsView; today: string }> {
  return send("PUT", "/me/dbs", input);
}

/** Everybody's DBS and what each needs. Owner only, and a separate call from
 * the staff list so that "who works here" never carries certificate numbers. */
export async function getStaffDbs(): Promise<StaffDbsList> {
  return get<StaffDbsList>("/admin/staff/dbs");
}

/** What the student list's filter menus may offer. Scoped like the list itself. */
export interface StudentFilterOptions {
  tutors: { id: string; name: string }[];
  instruments: string[];
  schools: string[];
  statuses: string[];
}

/**
 * The filter menus, fetched once when the page opens.
 *
 * Separate from the list so that narrowing the list does not narrow the menus:
 * a filter you cannot undo because its own option has vanished is worse than
 * no filter at all.
 */
export async function getStudentFilterOptions(): Promise<StudentFilterOptions> {
  return get<StudentFilterOptions>("/admin/students/filters");
}

/**
 * What colour this tutor's lessons are drawn in. Owner only.
 *
 * Null clears it, and their lessons go back to the house accent. Cosmetic, so
 * nothing else in the portal changes when it does.
 */
export async function setTutorColour(
  tutorProfileId: string,
  colour: string | null,
): Promise<{ colour: string | null }> {
  return send("PUT", `/admin/staff/${tutorProfileId}/colour`, { colour });
}

/** When a tutor is available to teach, week in, week out. Owner only. */
export async function getWorkingHours(
  tutorProfileId: string,
): Promise<{ periods: WorkingPeriod[] }> {
  return get(`/admin/staff/${tutorProfileId}/working-hours`);
}

/**
 * Replace a tutor's whole week. Owner only.
 *
 * What comes back is what was stored, which is not always what was sent:
 * overlapping stretches are joined and unusable ones dropped on the way in.
 * Callers should render the answer rather than what they asked for.
 */
export async function setWorkingHours(
  tutorProfileId: string,
  periods: WorkingPeriod[],
): Promise<{ periods: WorkingPeriod[] }> {
  return send("PUT", `/admin/staff/${tutorProfileId}/working-hours`, { periods });
}

/** Say somebody has seen the original certificate. Owner only. */
export async function verifyDbs(tutorProfileId: string,
  observed: Pick<DbsView, "certificateNumber" | "issuedOn" | "level">,
): Promise<{ dbs: DbsView }> {
  const { certificateNumber, issuedOn, level } = observed;
  return send("POST", `/admin/staff/${tutorProfileId}/dbs/verify`, {
    observedDocument: { certificateNumber, issuedOn, level },
  });
}

/** Write down what the Update Service said. Append-only. Owner only. */
export async function recordDbsCheck(
  tutorProfileId: string,
  outcome: DbsCheckOutcome,
  note?: string | null,
): Promise<{ dbs: DbsView }> {
  return send("POST", `/admin/staff/${tutorProfileId}/dbs/checks`, { outcome, note: note ?? null });
}

/** Everybody who teaches here, and everybody who has been asked to. Owner only. */
export async function getStaff(): Promise<StaffList> {
  return get<StaffList>("/admin/staff");
}

/**
 * Invite somebody to teach. Creates no account: the link is what does that.
 *
 * `tutorProfileId` names an existing tutor, and changes what accepting does —
 * their account moves onto the invited address instead of a second one being
 * made beside it. Omit it for somebody new.
 */
export async function inviteTutor(
  email: string,
  displayName: string,
  tutorProfileId?: string | null,
  bookingSetup?: TutorBookingSetup | null,
): Promise<StaffInvitation> {
  const result = await send<{ invitation: StaffInvitation }>("POST", "/admin/staff/invitations", {
    email,
    displayName,
    tutorProfileId: tutorProfileId ?? null,
    ...(bookingSetup ? { bookingSetup } : {}),
  });
  return result.invitation;
}

/** Withdraw an invitation that has not been used. The link stops working. */
export async function revokeInvitation(id: string): Promise<void> {
  await send("POST", `/admin/staff/invitations/${id}/revoke`);
}

/**
 * Add a student who is not in the booking system. Owner only.
 *
 * Says back which confirmed accounts were attached — "added, and their mum can
 * already see them" and "added, nobody can see them yet" are different
 * outcomes, and only one of them leaves somebody still to invite.
 */
export async function createStudent(
  student: Partial<StudentEditable> & { fullName: string; inviteToHub?: boolean },
): Promise<{
  student: StudentEditable & { id: string };
  linkedAccounts: string[];
  hubAccess?: StudentHubInvitations;
}> {
  return send("POST", "/admin/students", student);
}

/** Read a pasted list and say what would happen. Writes nothing. Owner only. */
export async function previewImport(text: string): Promise<ImportPreview> {
  return send<ImportPreview>("POST", "/admin/students/import/preview", { text });
}

/** Create the rows chosen from a preview, in one go. Owner only. */
export async function commitImport(
  students: ImportedStudent[],
): Promise<{ created: { id: string; fullName: string; linkedAccounts: string[]; hubAccessStatus?: string }[] }> {
  return send("POST", "/admin/students/import", { students });
}

/**
 * Correct a student's record. Owner only.
 *
 * A patch: send what changed. Omitting a field leaves it alone; sending null
 * clears it. The two are not the same, and the caller has to mean one of them.
 */
export async function updateStudent(
  id: string,
  changes: Partial<StudentEditable>,
): Promise<StudentEditable> {
  const result = await send<{ student: StudentEditable }>(
    "PATCH",
    `/admin/students/${id}`,
    changes,
  );
  return result.student;
}

export interface TeachingAssignmentEvidence {
  primaryTutor: boolean;
  futureApprovedLessons: number;
  historicalLessons: number;
  conflictingTutors: string[];
  conflictingServices: string[];
}

export interface TeachingAssignmentCandidate {
  id: string;
  organisationId: string;
  domain: "private";
  candidateKey: string;
  studentId: string;
  studentName: string;
  tutorProfileId: string | null;
  tutorName: string | null;
  verifiedProviderId: number | null;
  serviceId: number | null;
  serviceName: string | null;
  instrument: string | null;
  startsAt: string;
  endsAt: string | null;
  confidence: "low" | "ambiguous";
  evidence: TeachingAssignmentEvidence;
  source: string;
  status: "pending";
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  reviewReason: string | null;
  autoActivated: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface TeachingAssignmentCandidateList {
  candidates: TeachingAssignmentCandidate[];
  page: number;
  limit: number;
  total: number;
}

export interface TeachingAssignment {
  id: string;
  organisationId: string;
  domain: "private";
  studentId: string;
  tutorProfileId: string;
  tutorName: string | null;
  verifiedProviderId: number;
  serviceId: number | null;
  serviceName: string | null;
  instrument: string | null;
  startsAt: string;
  endsAt: string | null;
  status: "active" | "ended" | "pending-review" | "rejected";
  source: string;
  createdByUserId: string | null;
  createdReason: string;
  endedAt: string | null;
  endedByUserId: string | null;
  endedReason: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface TeachingAssignmentList {
  assignments: TeachingAssignment[];
}

export interface StudentTeacher {
  tutorProfileId: string;
  tutorName: string;
  instrument: string | null;
  assignments: { id: string; version: number }[];
}
export function getStudentTeachers(studentId: string) { return get<{ teachers: StudentTeacher[]; legacyTeacher?: { tutorProfileId: string; tutorName: string } | null }>(`/admin/students/${encodeURIComponent(studentId)}/teachers`); }
export function getStudentTeacherOptions(studentId: string, tutorProfileId: string) { return get<{ instruments: string[] }>(`/admin/students/${encodeURIComponent(studentId)}/teachers/options?tutorProfileId=${encodeURIComponent(tutorProfileId)}`); }
export interface TeacherAccessImpact { losingTeachers: { tutorProfileId: string; tutorName: string }[]; fingerprint: string; }
export function previewStudentTeacher(studentId: string, input: { tutorProfileId: string; instrument: string; previous?: StudentTeacher }) { return send<{ accessImpact: TeacherAccessImpact }>("POST", `/admin/students/${encodeURIComponent(studentId)}/teachers/preview`, input); }
export function saveStudentTeacher(studentId: string, input: { tutorProfileId: string; instrument: string; previous?: StudentTeacher; accessConfirmation?: string }) { return send("POST", `/admin/students/${encodeURIComponent(studentId)}/teachers`, input); }
export function clearStudentLegacyTeacher(studentId: string, previousTutorProfileId: string) { return send("POST", `/admin/students/${encodeURIComponent(studentId)}/teachers/legacy-remove`, { previousTutorProfileId }); }
export function removeStudentTeacher(studentId: string, previous: StudentTeacher) { return send("POST", `/admin/students/${encodeURIComponent(studentId)}/teachers/remove`, { instrument: previous.instrument, previous }); }

export interface TeacherHandoverSelection {
  goalIds: string[];
  repertoireIds: string[];
  practiceTodoIds: string[];
  appointmentIds: string[];
}
export interface TeacherHandoverGoal {
  id: string;
  title: string;
  description: string | null;
  status: string;
  targetDate: string | null;
}
export interface TeacherHandoverRepertoire {
  id: string;
  title: string;
  composer: string | null;
  instrument: string | null;
  status: string;
}
export interface TeacherHandoverPracticeTask {
  id: string;
  title: string;
  description: string | null;
}
export interface TeacherHandoverLesson {
  id: string;
  startsAt: string;
  endsAt: string;
  serviceName: string | null;
  tutorName: string | null;
}
export interface TeacherHandoverContext {
  goals: TeacherHandoverGoal[];
  repertoire: TeacherHandoverRepertoire[];
  practiceTasks: TeacherHandoverPracticeTask[];
  upcomingLessons: TeacherHandoverLesson[];
}
export interface TeacherHandoverPreview {
  source: {
    sourceAssignmentId: string;
    sourceAssignmentVersion: number;
    sourceTutorProfileId: string;
    sourceTutorName: string;
    assignments: { id: string; version: number }[];
    studentName: string;
    instrument: string;
  };
  context: TeacherHandoverContext;
}
export interface TeacherHandoverListItem {
  id: string;
  sourceAssignmentId: string;
  destinationTutorProfileId: string;
  destinationTutorName: string;
  instrument: string;
  effectiveDate: string;
  summary: string;
  createdAt: string;
  revokedAt: string | null;
  assignmentTransfer: "not_scheduled" | "scheduled" | "effective" | "cancelled";
}
export interface TeacherHandover {
  id: string;
  studentName: string;
  instrument: string;
  sourceTutorName: string;
  destinationTutorName: string;
  effectiveDate: string;
  summary: string;
  createdAt: string;
  context: TeacherHandoverContext;
  appointmentTransfer: "not_requested";
  assignmentTransfer: "not_scheduled" | "scheduled" | "effective" | "cancelled";
}
export function getTeacherHandoverConfig() {
  return get<{ enabled: boolean }>("/admin/teacher-handovers/config");
}
export function listStudentTeacherHandovers(studentId: string) {
  return get<{ items: TeacherHandoverListItem[] }>(
    "/admin/students/" + encodeURIComponent(studentId) + "/teacher-handovers",
  );
}
export function previewTeacherHandover(
  studentId: string,
  input: {
    sourceAssignmentId: string;
    sourceAssignmentVersion: number;
    sourceAssignments: { id: string; version: number }[];
  },
) {
  return send<TeacherHandoverPreview>(
    "POST",
    "/admin/students/" + encodeURIComponent(studentId) + "/teacher-handovers/preview",
    input,
  );
}
export function createTeacherHandover(
  studentId: string,
  input: {
    sourceAssignmentId: string;
    sourceAssignmentVersion: number;
    sourceAssignments: { id: string; version: number }[];
    destinationTutorProfileId: string;
    effectiveDate: string;
    summary: string;
    selectedContext: TeacherHandoverSelection;
    idempotencyKey: string;
  },
) {
  return send<{
    id: string;
    destinationAssignmentId: string | null;
    created: boolean;
    destinationTutorName?: string;
    studentName?: string;
    sourceTutorName?: string;
    instrument?: string;
    effectiveDate: string;
    appointmentTransfer: "not_requested";
    assignmentTransfer: "not_scheduled" | "scheduled" | "effective" | "cancelled";
    sharePath: string;
  }>(
    "POST",
    "/admin/students/" + encodeURIComponent(studentId) + "/teacher-handovers",
    input,
  );
}
export function getTeacherHandover(id: string) {
  return get<TeacherHandover>("/admin/teacher-handovers/" + encodeURIComponent(id));
}
export function revokeTeacherHandover(id: string) {
  return send<{
    revoked: boolean;
    assignmentTransfer?: "not_scheduled" | "scheduled" | "effective" | "cancelled";
  }>(
    "POST",
    "/admin/teacher-handovers/" + encodeURIComponent(id) + "/revoke",
    {},
  );
}


export interface TeachingAssignmentReviewInput {
  decision: "approve" | "reject";
  reason: string;
  studentId?: string;
  tutorProfileId?: string;
  verifiedProviderId?: number;
  serviceId?: number;
  serviceName?: string;
  /** Kept for the server contract; the screen derives this from the service name. */
  instrument?: string;
  startsAt?: string;
  endsAt?: string | null;
}

export interface TeachingAssignmentReviewResult {
  candidateId: string;
  assignmentId: string | null;
}

export async function getTeachingAssignmentCandidates(
  page = 1,
): Promise<TeachingAssignmentCandidateList> {
  return get<TeachingAssignmentCandidateList>("/admin/teaching-assignment-candidates", {
    confidence: "low,ambiguous",
    page: String(page),
  });
}

export async function reviewTeachingAssignmentCandidate(
  candidateId: string,
  input: TeachingAssignmentReviewInput,
): Promise<TeachingAssignmentReviewResult> {
  return send<TeachingAssignmentReviewResult>(
    "POST",
    `/admin/teaching-assignment-candidates/${encodeURIComponent(candidateId)}/review`,
    input,
  );
}

export async function getStudentTeachingAssignments(
  studentId: string,
): Promise<TeachingAssignmentList> {
  return get<TeachingAssignmentList>(
    `/admin/students/${encodeURIComponent(studentId)}/teaching-assignments`,
  );
}

export async function endTeachingAssignment(
  studentId: string,
  assignmentId: string,
  input: { reason: string; version: number },
): Promise<{ assignment: TeachingAssignment }> {
  return send<{ assignment: TeachingAssignment }>(
    "POST",
    `/admin/students/${encodeURIComponent(studentId)}/teaching-assignments/${encodeURIComponent(assignmentId)}/end`,
    input,
  );
}

// ── Response shapes ────────────────────────────────────────────────────────
// Hand-written rather than shared with the server. When the API grows a
// generated client this is the file that goes away.

export type Role = "owner" | "tutor" | "student" | "parent";

export interface Me {
  studentAwardsEnabled?: boolean;
  /** Owner-only concert management is served only while the server flag is on. */
  enquiryPipelineEnabled?: boolean;
  concertsEnabled?: boolean;
  /** Family invitation reads appear only for linked accounts while the event feature is enabled. */
  concertInvitationsEnabled?: boolean;
  /** Family group-activity pages are served only while the server feature flag is on. */
  groupActivitiesEnabled?: boolean;
  /** Staff attendance surfaces are available only while the group-activity flag is on. */
  groupActivityAttendanceEnabled?: boolean;
  /** Server-controlled pilot for safe tutor attendance batching. */
  teachingDayBulkAttendanceEnabled?: boolean;
  /** Server flag: the weekly practice goal is stored on the account rather than in this browser. */
  practiceWeeklyGoalEnabled?: boolean;
  /** Server flag: Practice History shows the personal insights panel. */
  practiceInsightsEnabled?: boolean;
  /** Server flag: assigned tasks link into the metronome and Groove with learner, instrument and a way back. */
  practiceToolLinksEnabled?: boolean;
  /** Server-controlled pilot access, scoped to the current account and tutor. */
  paymentInvoices?: boolean;
  user: { id: string; email: string | null; username: string | null };
  roles: Role[];
  /** Server-derived authority for Hub-owned business domains. */
  authorities: { schoolDomain: boolean };
  verified: boolean;
  studentIds: string[];
  childStudentIds: string[];
  /**
   * Which halves of the booking system are configured. Both are optional and
   * independent, so a screen is left out rather than drawn over a 404.
   */
  booking: {
    familyCredits?: boolean;
    /** Where families buy lesson credits; null when the Hub is not configured with one. */
    purchaseUrl?: string | null;
    /** Off by default until the household overview is reviewed for rollout. */
    householdOverview?: boolean;
    staff: boolean;
    student: boolean;
    /** Whether the global family Lessons/student-booking feature is enabled. */
    studentFeatureEnabled?: boolean;
    /** Fixed authenticated tutor/provider context for scoped staff actions. */
    providerId?: number | null;
  };
  /**
   * What this account may do in the booking system, in FastAPI's vocabulary.
   *
   * Used to decide which screens are worth offering, never to decide whether a
   * call succeeds — FastAPI checks again, and it is the one that knows.
   */
  capabilities: string[];
  /**
   * Whether this account is linked to a teacher in the booking system.
   *
   * Separate from the capabilities above, and it has to be: a capability says
   * what somebody is allowed to ask for, and this says which questions their
   * account can be the subject of. "My own figures" is scoped by Amelia
   * provider, so an office account with no provider cannot ask it however much
   * it has been granted.
   */
  linkedToProvider: boolean;
  /**
   * How many departed tutors' DBS records are past their retention period.
   *
   * Zero for anybody who is not an owner. Here rather than behind its own call
   * because erasing is the one obligation in the portal with no natural moment
   * to be noticed — the people it concerns have left, so they are on no list
   * anybody opens — and `/me` is already re-read when the window comes back to
   * the front, so the badge catches up without anybody reloading.
   */
  dbsDueForErasure: number;
}

export type DbsLevel = "basic" | "standard" | "enhanced" | "enhanced-barred";

/**
 * What a DBS record means today.
 *
 * Worked out by the server on every read rather than stored, so it is never
 * the answer from the morning it was written.
 */
export type DbsStatus =
  | "missing"
  | "unverified"
  | "not-subscribed"
  | "lapsed"
  | "renewal-due"
  | "valid";

export type DbsCheckOutcome = "clear" | "new-information" | "not-found" | "lapsed";

/** What a tutor types in. The certificate, and the subscription that keeps it live. */
export interface DbsInput {
  certificateNumber: string;
  issuedOn: string;
  level: DbsLevel;
  updateServiceSubscribed: boolean;
  updateServiceRenewsOn: string | null;
}

export interface DbsView extends DbsInput {
  status: DbsStatus;
  /** When somebody saw the original certificate, and who. Null until they have. */
  verifiedAt: string | null;
  verifiedBy: string | null;
  updatedAt: string;
  lastCheck: { checkedAt: string; outcome: DbsCheckOutcome; by: string | null } | null;
}

/**
 * Whether a tutor has agreed we may check them on the Update Service.
 *
 * `given` is null when nobody has asked, which is not the same as asked and
 * refused — and the difference is the only thing anybody wants from this.
 */
export interface DbsCheckConsent {
  given: boolean | null;
  at: string | null;
}

export interface StaffDbsRow {
  tutorProfileId: string;
  displayName: string;
  email: string | null;
  status: DbsStatus;
  dbs: DbsView | null;
  consent: DbsCheckConsent;
}

/**
 * A departed tutor whose DBS the school still holds.
 *
 * Kept apart from `tutors`, which is who needs checking. These people need
 * nothing checked; what they need is erasing once the retention period ends.
 */
export interface DbsErasureRow {
  tutorProfileId: string;
  displayName: string;
  leftOn: string;
  retentionEndsOn: string;
  due: boolean;
}

export interface StaffDbsList {
  /** The server's idea of today, in the school's timezone. */
  today: string;
  tutors: StaffDbsRow[];
  erasure: DbsErasureRow[];
}

/** Erase a departed tutor's DBS record and its check history. Owner only. */
export async function eraseDbs(
  tutorProfileId: string,
): Promise<{ erasure: DbsErasureRow[] }> {
  return send("DELETE", `/admin/staff/${tutorProfileId}/dbs`, undefined);
}

/** An invitation, as the accept page sees it before spending anything. */
export interface Invitation {
  email: string;
  /** The name the school typed when inviting; the tutor can correct it. */
  displayName: string;
  /**
   * False when the address already has an account somebody uses. The page must
   * not ask for a password in that case — theirs already works, and the server
   * will not replace it.
   */
  needsPassword: boolean;
}

/**
 * Where a tutor has got to.
 *
 * "never-signed-in" is the one worth drawing attention to: the accounts adopted
 * from the booking provider were made with a password nobody was told, and
 * until now nothing said so.
 */
export type StaffAccountState = "active" | "never-signed-in" | "invited" | "left";

export interface StaffMember {
  bookingProfile?: { status: "pending" | "processing" | "linked" | "needs_attention"; message: string | null; creationAttemptedAt: string | null; setup: TutorBookingSetup } | null;
  userId: string;
  /**
   * Their tutor profile, or null for staff who do not teach — an owner who
   * runs the school without taking lessons has no profile at all, and the
   * actions that need one are not offered for them.
   */
  tutorProfileId: string | null;
  displayName: string;
  /** What their lessons are drawn in on the diary, or null where nobody has chosen. */
  colour: string | null;
  email: string | null;
  externalProviderId: number | null;
  lastLoginAt: string | null;
  emailConfirmedAt: string | null;
  /** When they stopped teaching here, or null while they still do. */
  leftAt: string | null;
  /** "owner", "tutor", or both. Empty for somebody who has left. */
  roles: Role[];
  /**
   * What they may do in the booking system beyond their own lessons.
   *
   * Always empty for an owner, who holds everything by being one — the screen
   * says so rather than drawing an unticked list for the person with the most
   * access. Rows kept for somebody who has left are worth nothing until they
   * are put back, and the server sends them so the page can say that too.
   */
  capabilities: string[];
  state: StaffAccountState;
}

/** One capability, and what granting it would let somebody do. */
export interface CapabilityInfo {
  capability: string;
  description: string;
}

/** Add missing Standard tutor defaults while preserving every current grant. */
export function addStandardTutorPrivileges(userId: string): Promise<{ capabilities: string[] }> {
  return send("POST", `/admin/staff/${encodeURIComponent(userId)}/capabilities/standard-tutor`, {});
}

/**
 * Say what somebody may do in the booking system. Replaces the whole set.
 *
 * Refused for an owner, for somebody who has left, and for anybody holding no
 * staff role — each with a message worth showing, because each is a state the
 * page can be looking at when the owner ticks a box.
 */
export async function setCapabilities(
  userId: string,
  capabilities: string[],
): Promise<string[]> {
  const result = await send<{ capabilities: string[] }>(
    "PUT",
    `/admin/staff/${userId}/capabilities`,
    { capabilities },
  );
  return result.capabilities;
}

/** Mark somebody as no longer teaching. Their notes stay; their access goes. */
export async function markTutorAsLeft(tutorProfileId: string): Promise<void> {
  await send("POST", `/admin/staff/${tutorProfileId}/leave`);
}

/** Put somebody back. They sign in again with the password they already had. */
export async function reinstateTutor(tutorProfileId: string): Promise<void> {
  await send("POST", `/admin/staff/${tutorProfileId}/reinstate`);
}

export interface StaffInvitation {
  id: string;
  email: string;
  displayName: string;
  /** Set when this reaches an existing tutor rather than somebody new. */
  tutorProfileId: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface StaffList {
  bookingSetupEnabled?: boolean;
  tutors: StaffMember[];
  /** Outstanding only — accepted, withdrawn and expired ones are not listed. */
  invitations: StaffInvitation[];
  /**
   * Every capability there is, from the server rather than from a copy here.
   *
   * The names belong to the booking system's contract. A list maintained in the
   * browser would eventually offer one the server refuses to store, and the
   * person who found out would be an owner who thought they had granted it.
   */
  catalogue: CapabilityInfo[];
}

export interface TutorBookingSetup { firstName: string; lastName: string; locationId: number | null }
export interface TutorBookingOptions { locations: Array<{ id: number; name: string }> }
export function getTutorBookingOptions(): Promise<TutorBookingOptions> { return get("/admin/staff/booking-profile-options"); }
export function connectTutorBookingProfile(tutorProfileId: string, setup: TutorBookingSetup & { confirmNoEmployee?: boolean; reason?: string }): Promise<{ status: string; externalProviderId: number | null }> {
  return send("POST", `/admin/staff/${encodeURIComponent(tutorProfileId)}/booking-profile`, setup);
}

export interface StudentSummary {
  id: string;
  fullName: string;
  displayName?: string;
  studentEmail: string | null;
  parentEmail: string | null;
  parentName: string | null;
  instrument: string | null;
  status: string;
  isUnder18: boolean;
  tutorId: string | null;
  /** Null when nobody is assigned, which is a state worth seeing. */
  tutorName: string | null;
  /** The directory keeps both persisted relationship types separate. */
  hubAccess: StudentHubAccessSummary;
  /**
   * What they actually learn, from their bookings — one entry per instrument
   * and teacher. A student taking two instruments from two tutors has two,
   * which the single `instrument` and `tutorName` fields cannot express.
   */
  enrolments: Enrolment[];
}

export interface StudentHubAccessSummary {
  studentAccountLinked: boolean;
  familyAccountCount: number;
}

export interface HubAccountView {
  userId: string;
  email: string | null;
  username: string | null;
  confirmed: boolean;
  lastActiveAt: string | null;
}

export interface StudentHubAccess {
  studentAccount: HubAccountView | null;
  familyAccounts: HubAccountView[];
  recordAddresses: {
    studentEmail: string | null;
    parentEmail: string | null;
    secondaryEmail: string | null;
    schoolEmail: string | null;
  };
  matchingAccounts?: {
    userId: string;
    email: string;
    username: string | null;
    relationship: "student" | "parent";
    matchedBy: string[];
    directAccountClaimed: boolean;
    linked: boolean;
  }[];
}

/** Tutor-safe detail projection with no Hub account identifiers or activity. */
export interface StaffStudentHubAccess {
  studentAccountLinked: boolean;
  familyAccountCount: number;
  familyAccountEmails: string[];
}

export type StudentHubAccessProjection = StudentHubAccess | StaffStudentHubAccess;

export type HubInvitationAddressState =
  | "linked"
  | "account_unlinked"
  | "account_unconfirmed"
  | "invitation_pending"
  | "no_account";

export interface HubInvitationAddress {
  email: string;
  role: "student" | "parent" | "secondary";
  state: HubInvitationAddressState;
  invitationExpiresAt?: string;
  invitationId?: string;
}

export interface StudentHubInvitations {
  status: "linked_existing" | "invitation_pending" | "invitation_available" | "needs_contact" | "conflict";
  addresses: HubInvitationAddress[];
  defaultRecipients: string[];
  invited?: string[];
  mailFailed?: string[];
  invitationsEnabled?: boolean;
}

export async function sendHubInvitations(
  studentId: string,
  recipients?: string[],
  resend = false,
): Promise<{ hubAccess: StudentHubInvitations }> {
  return send("POST", `/admin/students/${encodeURIComponent(studentId)}/hub-invitations`, {
    ...(recipients === undefined ? {} : { recipients }),
    resend,
  });
}

export async function revokeHubInvitation(invitationId: string): Promise<void> {
  await send("POST", `/admin/hub-invitations/${encodeURIComponent(invitationId)}/revoke`);
}

export interface Enrolment {
  instrument: string | null;
  serviceName: string | null;
  tutorName: string | null;
  lessons: number;
}

export interface StudentList {
  students: StudentSummary[];
  page: number;
  limit: number;
  total: number;
}

/** One student as read out of a spreadsheet. */
export interface ImportedStudent {
  fullName: string;
  parentName: string | null;
  parentEmail: string | null;
  studentEmail: string | null;
  parentPhone: string | null;
  instrument: string | null;
  school: string | null;
  formClass: string | null;
  isUnder18: boolean;
}

/** What would happen to one row, beyond whether it could be read at all. */
export type RowVerdict = "new" | "already-here" | "repeated" | "unusable";

export interface ImportRow {
  /** The line in the pasted text, counting the headings. */
  line: number;
  student: ImportedStudent | null;
  problems: string[];
  verdict: RowVerdict;
  /** The existing student, where this row matched one. */
  existingId: string | null;
}

export interface ImportPreview {
  /** Which heading each field was read from, so it can be shown back. */
  mapping: Partial<Record<keyof ImportedStudent, string>>;
  /** Headings that matched nothing. Shown rather than silently dropped. */
  ignored: string[];
  rows: ImportRow[];
  counts: Record<RowVerdict, number>;
  /** Set when nothing can be done with the text at all. */
  fatal: string | null;
}

/**
 * The fields the record page can correct.
 *
 * Named explicitly rather than left in the record's catch-all, because a form
 * that binds to `Record<string, unknown>` silently keeps compiling when a
 * column is renamed and silently stops saving anything.
 */
export interface StudentEditable {
  fullName: string;
  instrument: string | null;
  tutorId: string | null;
  studentEmail: string | null;
  parentName: string | null;
  parentEmail: string | null;
  secondaryEmail: string | null;
  parentPhone: string | null;
  isUnder18: boolean;
  school: string | null;
  formClass: string | null;
  /**
   * Null means "not stated" — the existing inference (a parent address, then
   * the student's own) still applies. Attending a school is not the same as
   * that school paying, so this is never set from `school` on the same
   * record.
   */
  billingPayer: "family" | "school" | "self" | null;
}

/**
 * Who pays for a student's private lessons, and whether the booking system
 * already knows them.
 *
 * The Hub's own decision, projected for a screen — see
 * `apps/hub/src/booking/setup.ts`. **Rendered, never recomputed.** Whether a
 * payer can be provisioned depends on sibling links and school payer records
 * the browser cannot see, and a second opinion formed here from the fields on
 * the record would be a second identity rule that disagrees with the first.
 */
export type PrivateBookingState = "linked" | "not_set_up" | "needs_details" | "not_required";

/** Stable across wording changes; the page branches on these, not on prose. */
export type PayerUnavailableReason =
  | "no_payer_on_record"
  | "no_payer_email"
  | "payer_name_incomplete"
  | "school_not_configured"
  | "payer_link_conflict";

export interface PrivateBookingSetup {
  payer: { kind: "family" | "school" | "self"; display_name: string } | null;
  state: PrivateBookingState;
  reason: PayerUnavailableReason | null;
}

export const getBookingSetup = (studentId: string): Promise<PrivateBookingSetup> =>
  get<PrivateBookingSetup>(`/admin/students/${studentId}/booking-setup`);

/**
 * Extra addresses a student's notes go to, and what this account may do about
 * them. The server decides both flags; the page only draws them.
 */
export interface NoteRecipientList {
  canAdd: boolean;
  recipients: {
    id: string;
    email: string;
    createdAt: string;
    /** Who added it, where that account still exists. */
    addedBy: string | null;
    canRemove: boolean;
  }[];
}

/** What a student has logged, and how it adds up. */
/**
 * A running timer, as the server sees it.
 *
 * Absolute instants, and never an elapsed count. The page is counting towards
 * a deadline the server will enforce whatever the browser believes, so it is
 * given the deadline itself along with `now` to measure its own clock against.
 */
export interface PracticeTimer {
  startedAt: string;
  instrument?: string | null;
  /** When it will ask whether anybody is still practising. */
  promptAt: string;
  /** And when it will stop waiting for an answer. */
  givesUpAt: string;
  state: "running" | "asking" | "expired";
}

/**
 * Something hanging off a practice session: a file we hold, or a link we do
 * not. One shape for both, because in a list they are the same thing.
 */
export interface PracticeAttachment {
  id: string;
  kind: "file" | "youtube";
  /** The uploaded name, or the address that was pasted. */
  fileName: string;
  fileType: string | null;
  fileSize?: number | null;
  /** When the recording or link was added to practice history. */
  uploadedAt?: string | null;
  /** Set for a YouTube link, null for a file. */
  videoId: string | null;
  /** Private task context for an assigned submission. */
  practiceTodoId?: string | null;
  practiceTaskTitle?: string | null;
  /** Optional learner reflection saved with this evidence. */
  reflection?: string | null;
}

export interface PracticeSession {
  id: string;
  startedAt: string;
  durationSeconds: number;
  name: string | null;
  notes: string | null;
  needsWork: boolean;
  /** Filed by the timer because nobody answered it, so it has no description. */
  autoStopped: boolean;
  attachments: PracticeAttachment[];
  attachmentTasks?: Array<{ id: string; label: string }>;
  recordingsEnabled?: boolean;
}

export interface PracticeLog {
  lastSevenDaysSeconds: number;
  sessions: PracticeSession[];
  /** Cursor for the next page of older session detail, when available. */
  nextCursor?: string | null;
  hasMore?: boolean;
  timer: PracticeTimer | null;
  /** The server's clock when it answered. */
  now: string;
}

export interface StudentDetail {
  student: Omit<StudentSummary, "hubAccess"> &
    StudentEditable & {
      tutorName: string | null;
      hubAccess: StudentHubAccessProjection;
      hubInvitations: StudentHubInvitations | null;
      accountAccessRemovalEnabled?: boolean;
      familyOverviewAvailable?: boolean;
      /** Set only while a tutor-added record is still waiting to be read. */
      addedByName?: string | null;
    } & Record<string, unknown>;
  enrolments: Enrolment[];
  lessons: {
    id: string;
    title: string | null;
    lessonDate: string;
    attendanceStatus: string | null;
  }[];
  upcomingBookings: {
    appointmentId: string;
    /** Numeric Amelia id, when this is an Amelia-backed lesson. */
    ameliaAppointmentId: number | null;
    startsAt: string;
    endsAt: string;
    serviceName: string | null;
    status: string;
    isSchools: boolean;
  }[];
  /** Every lesson in the next booked London calendar week, without the history-page limit. */
  nextWeekBookings?: StudentDetail["upcomingBookings"];
  /**
   * How many of those the Amelia bulk actions can be given.
   *
   * Fewer than `upcomingBookings` whenever a Hub-native school lesson is ahead:
   * those have no Amelia appointment id, so "move all" and "cancel all" cannot
   * name them. Zero means neither action has anything to do.
   */
  ameliaFutureLessons: number;
  practice: { sessions: number; totalSeconds: number };
  /** What they have been doing between lessons, newest first. */
  recentPractice: {
    id: string;
    startedAt: string;
    durationSeconds: number;
    name: string | null;
    needsWork: boolean;
    autoStopped: boolean;
    /** Who counted the minutes. Null for sessions that predate the record. */
    loggedBy: "student" | "family" | null;
    attachments: PracticeAttachment[];
  }[];
  /** Whether the private student-record practice history has older pages. */
  recentPracticeHasMore?: boolean;
}

export interface Booking {
  appointmentId: string;
  externalId: number | null;
  startsAt: string;
  endsAt: string;
  serviceName: string | null;
  categoryName: string | null;
  status: string;
  isSchools: boolean;
  /** Canonical School scope. Present only on server-authorised School rows. */
  schoolKey?: string | null;
  externalProviderId: number | null;
  bookingId: string;
  bookingStatus: string;
  price: string | null;
  customerName: string | null;
  studentId: string | null;
  studentName: string | null;
  attributionSource: string | null;
  /**
   * Who is teaching it. Null where the appointment's tutor was never resolved,
   * or where their profile has since been deleted — the diary says so rather
   * than leaving the line blank.
   */
  tutorId: string | null;
  tutorName: string | null;
  /** What colour to draw it in. Null for a tutor nobody has given one. */
  tutorColour: string | null;
  /** Which instrument, for the shade. "Piano 30" and "Piano 60" share one. */
  instrument: string | null;
  /** Null when the lesson has not been written up yet. */
  lessonId: string | null;
  attendanceStatus: string | null;
  /** Null while the note is a draft, so the diary can say which. */
  lessonPublishedAt: string | null;
  /** Whether the family has been sent the note. */
  lessonNoteSent?: boolean;
}

export type Attendance = "present" | "absent" | "late" | "cancelled";

export interface Lesson {
  id: string;
  title: string;
  content: string | null;
  lessonDate: string | null;
  attendanceStatus: Attendance | null;
  /** Null while a draft; the moment the family could first see it. */
  publishedAt: string | null;
  notesSent: boolean;
  updatedAt: string;
}

export interface LessonEmailDelivery { total: number; queued: number; sending: number; sent: number; delivered: number; failed: number; bounced: number; complained: number; }

export interface BookingLesson {
  /** Server-controlled pilot for the separate staff-only lesson note field. */
  staffOnlyNotesEnabled?: boolean;
  /** Opt-in for local deterministic formatting on an authorized private lesson. */
  localLessonNoteFormattingEnabled?: boolean;
  neckDiagramInstrument?: import("@wmf/shared/neckDiagrams").InstrumentId | null;
  emailDelivery?: LessonEmailDelivery;
  booking: {
    bookingId: string;
    studentId: string | null;
    studentName: string | null;
    appointmentId: string;
    /** Amelia's appointment id, where this lesson has one. Absent on an older response. */
    ameliaAppointmentId?: number | null;
    /** This pupil's own Amelia booking within that appointment, where it has one. */
    ameliaBookingId?: number | null;
    startsAt: string;
    endsAt: string;
    serviceName: string | null;
  };
  lesson: Lesson | null;
  /**
   * Whether the portal has object storage. Absent on an older response, which
   * the page treats as "no", so a picker is never offered on a guess.
   */
  storageConfigured?: boolean;
}

/**
 * Somebody else's lesson, as much of it as a colleague may see.
 *
 * A separate type rather than a `Booking` with the names nulled, because that
 * is how the server sends it — built field by field, so that a column added to
 * the diary query later cannot arrive here by accident. Empty for an owner,
 * who reads every lesson in `bookings`.
 */
/**
 * Who a lesson could be for, as offered by the server.
 *
 * `matchedOn: "booker"` means the person who paid for this lesson also pays for
 * them — the sibling case, and the one worth showing first and labelling, since
 * "the family who booked this" is a much stronger hint than "somebody you
 * teach".
 */
export interface LessonStudentOption {
  id: string;
  fullName: string;
  matchedOn: "booker" | "taught";
}

export interface LessonStudentChoices {
  booking: {
    id: string;
    studentId: string | null;
    studentName: string | null;
    customerName: string | null;
    startsAt: string;
    serviceName: string | null;
  };
  candidates: LessonStudentOption[];
  /** This lesson plus every future one in the same weekly slot. */
  series: { count: number; label: string };
  /** Always true. Kept so an older page reading this still behaves. */
  canAddNew: boolean;
  /**
   * What a newly added child would inherit from this booking. A null address
   * means the record will have no contact details until an owner adds them —
   * worth saying before somebody types a name, not after.
   */
  addNew?: { address: string | null };
}

export interface LessonMoved {
  moved: number;
  notesMoved: number;
  notesAlreadySent: number;
  notesLeft: number;
  skipped: number;
  student: { id: string; fullName: string };
  /** True when the student on the lesson is one this action just made. */
  created: boolean;
  /** False when the child now on this lesson has no address on their record. */
  reachable?: boolean;
}

export interface StudentClash {
  studentId: string;
  studentName: string;
  ameliaCustomerId: number | null;
  startsAt: string;
  services: string[];
  tutors: string[];
  lessons: number;
}

/** Students booked into two lessons at one moment. Owner, or `reports.operations.read`. */
export function getStudentClashes(
  page = 1,
  limit = 15,
): Promise<{
  clashes: StudentClash[];
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}> {
  return get("/admin/reports/student-clashes", {
    page: String(page),
    limit: String(limit),
  });
}

/** Who this lesson could be for, and how big the standing lesson is. */
/** Mark a tutor-added record as read. Owner only. */
export async function confirmStudentRecord(studentId: string): Promise<void> {
  await send("POST", `/admin/students/${studentId}/confirm-record`);
}

export function lessonStudentChoices(bookingId: string): Promise<LessonStudentChoices> {
  return get<LessonStudentChoices>(`/admin/bookings/${bookingId}/students`);
}

/** Correct the existing learner's name without moving any history. Owner only. */
export function correctLessonStudentName(
  bookingId: string,
  changes: { studentId: string; expectedName: string; fullName: string },
): Promise<{ student: { id: string; fullName: string } }> {
  return send("POST", `/admin/bookings/${bookingId}/student/name`, changes);
}

/**
 * Put this lesson on a different student.
 *
 * `series` takes the rest of the weekly slot with it, which is almost always
 * what is wanted: whatever mis-filed this lesson mis-filed every one of them.
 */
export function moveLessonToStudent(
  bookingId: string,
  studentId: string,
  scope: "one" | "series",
): Promise<LessonMoved> {
  return send<LessonMoved>("POST", `/admin/bookings/${bookingId}/student`, { studentId, scope });
}

/**
 * Put this lesson on a student the portal has never heard of, creating them.
 *
 * A name and nothing else: the addresses and the booking account come from the
 * lesson, which is what makes this something a tutor may do. Throws with code
 * `student_exists` when that child is plainly already here, and
 * `student_similar` when the name merely resembles one — the second is answered
 * by sending it again with `confirmNew`.
 */
export function addStudentToLesson(
  bookingId: string,
  fullName: string,
  scope: "one" | "series",
  confirmNew = false,
): Promise<LessonMoved> {
  return send<LessonMoved>("POST", `/admin/bookings/${bookingId}/student`, {
    fullName,
    scope,
    confirmNew,
  });
}

export interface ColleagueLesson {
  appointmentId: string;
  startsAt: string;
  endsAt: string;
  instrument: string | null;
  serviceName: string | null;
  isSchools: boolean;
  tutorId: string | null;
  tutorName: string | null;
  tutorColour: string | null;
}

export interface BookingList {
  from: string;
  to: string;
  bookings: Booking[];
  others: ColleagueLesson[];
  /**
   * Everybody's working hours, keyed by tutor profile id.
   *
   * A tutor nobody has filled in is *absent* rather than an empty list, and
   * the calendar reads the difference: no rota means unknown, so nothing is
   * shaded; an empty rota would mean a week off.
   */
  rota: Record<string, { name: string; periods: WorkingPeriod[] }>;
}

export interface QueueSlot {
  label: string;
  weekday: string;
  time: string;
  serviceName: string | null;
  count: number;
  bookingIds: string[];
  firstLesson: string;
  lastLesson: string;
}

export interface QueueGroup {
  declaredName?: string | null;
  externalCustomerId: number | null;
  customerName: string | null;
  customerEmail: string | null;
  bookingIds: string[];
  nextLesson: string;
  count: number;
  candidates: {
    id: string;
    fullName: string;
    studentEmail: string | null;
    parentEmail: string | null;
    secondaryEmail: string | null;
    /** "email" is near-certain; "name" is a suggestion to be confirmed. */
    matchedOn: "email" | "name" | "customer-id" | "current";
  }[];
  confirmationCount?: number;
  currentPlacements?: { studentId: string; fullName: string }[];
  slots: QueueSlot[];
}

/** A student a tutor added from a lesson, waiting for an owner to read it. */
export interface UncheckedStudent {
  id: string;
  fullName: string;
  addedByName: string | null;
  parentEmail: string | null;
  createdAt: string;
}

export interface UnidentifiedQueueSlot {
  label: string;
  firstLesson: string;
  lastLesson: string;
  bookingIds: string[];
  count: number;
}

/** One typed student name from one booker; its weekly slots are in `slots`. */
export interface UnidentifiedQueueRow extends UnidentifiedQueueSlot {
  declaredName: string | null;
  /** Absent from servers before one-row-per-name; treat the row as one slot. */
  slots?: UnidentifiedQueueSlot[];
}

export type UnidentifiedQueueGroup = Omit<QueueGroup, "candidates" | "slots"> & {
  rows: UnidentifiedQueueRow[];
};

export interface Queue {
  recentlyAutoCreated?: { id: string; fullName: string; displayName?: string; parentEmail: string | null; createdAt: string }[];
  /** Empty for a tutor: checking your own typing is not a check. */
  addedByTutors: UncheckedStudent[];
  needsDecision: QueueGroup[];
  unidentified: {
    groups: number;
    bookings: number;
    sample: UnidentifiedQueueGroup[];
  };
  /** Metadata describes the complete queue, not only the visible page. */
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
  addedByTutorsPage?: number;
  addedByTutorsLimit?: number;
  addedByTutorsTotal?: number;
  addedByTutorsTotalPages?: number;
  summary?: {
    totalBookings: number;
    readyGroups: number;
    readyBookings: number;
    unidentifiedGroups: number;
    unidentifiedBookings: number;
    addedByTutors: number;
  };
  truncated?: boolean;
}

// ── The family side ────────────────────────────────────────────────────────

export interface MyStudent {
  id: string;
  fullName: string;
  instrument: string | null;
  tutorName: string | null;
  /** True for the account holder, false for one of their children. */
  isSelf: boolean;
  /**
   * Whether the private booking system can act for this student.
   *
   * False for a School pupil, whose timetable the school sets rather than the
   * family. Separate from `me.booking.student`, which says only whether the
   * booking integration is configured at all: both have to be true before a
   * family is offered booking or payment screens.
   */
  privateBooking: boolean;
  /** Independent School membership/billing entitlement; optional during rollout. */
  schoolInvoicing?: boolean;
}

/**
 * A file a tutor attached to a lesson note.
 *
 * `url` is served by the Hub, which re-checks that this family may see the
 * note before redirecting to a short-lived storage address. The storage key
 * itself never reaches the browser.
 */
export interface LessonNoteFile {
  id: string;
  fileName: string;
  fileType: string | null;
  fileSize: number | null;
  resourceType: string | null;
  url: string;
}

export interface LessonNoteGroove {
  id: string;
  title: string;
  token: string;
}

export interface MyStudentDetail {
  student: { id: string; fullName: string; instrument: string | null; tutorName: string | null };
  /** Present only for explicit `lessonNotesPage` reads; older callers keep the legacy response. */
  lessonNotesPage?: {
    page: number;
    pageSize: number;
    total: number;
    pages: number;
    hasMore: boolean;
    /** Visible lesson date immediately before this page, used for practice-task attribution. */
    newerLessonDate?: string | null;
  };
  lessons: {
    id: string;
    sourceId?: string;
    domain?: "private" | "school";
    updatedAt?: string;
    title: string;
    content: string | null;
    lessonDate: string | null;
    attendanceStatus: string | null;
    tutorName: string | null;
    /** Absent on older responses; treat a missing list as an empty one. */
    attachments?: LessonNoteFile[];
    neckDiagrams?: { id: string; title: string; document: import("@wmf/shared/neckDiagrams").NeckDiagramSheet }[];
    grooves?: LessonNoteGroove[];
  }[];
  upcomingBookings: {
    appointmentId: string;
    startsAt: string;
    endsAt: string;
    serviceName: string | null;
    tutorName: string | null;
    /** Requested but not yet approved by the tutor or the booking system. */
    awaitingConfirmation?: boolean;
  }[];
  practice: { sessions: number; totalSeconds: number };
}

export interface SyncStatus {
  enabled: boolean;
  intervalMinutes: number;
  running: boolean;
  last: {
    trigger: string;
    startedAt: string;
    finishedAt: string | null;
    appointmentsSeen: number;
    bookingsSeen: number;
    matched: number;
    unresolved: number;
    skippedWaitlistCancellations: number;
    error: string | null;
  } | null;
}

export interface Tutor {
  id: string;
  displayName: string;
  externalProviderId: number | null;
  /** They no longer teach here. Offered only where already assigned. */
  left: boolean;
}

/**
 * One student on a tutor's roll, folded across every service they take from
 * them — a 30 and a 60 minute drum lesson are one line, not two.
 */
export interface TutorStudent {
  id: string;
  fullName: string;
  /** What they learn from this tutor. Empty where no service was recorded. */
  instruments: string[];
  lessons: number;
  /** The most recent lesson with this tutor, or null if all of them are ahead. */
  lastLesson: string | null;
}

export interface TutorProfile {
  tutor: {
    id: string;
    displayName: string;
    email: string | null;
    /** Their id in the booking system, where they have one. */
    externalProviderId: number | null;
    lastLoginAt: string | null;
    /** When they stopped teaching here, or null while they still do. */
    leftAt: string | null;
    createdAt: string;
    roles: Role[];
    state: StaffAccountState;
  };
  totals: {
    /** Bookings in the diary, cancellations excluded. */
    lessons: number;
    students: number;
    notesWritten: number;
    /** Written up but never published, so no family has seen them. */
    drafts: number;
  };
  students: TutorStudent[];
  upcoming: {
    bookingId: string;
    appointmentId: string;
    startsAt: string;
    serviceName: string | null;
    isSchools: boolean;
    studentId: string | null;
    studentName: string | null;
    /** Who booked, for a lesson whose student was never matched. */
    customerName: string | null;
  }[];
  upcomingHasMore: boolean;
}

/**
 * One tutor in full. Owners may read anybody's; a tutor may read their own.
 *
 * That is the only one they can reach anyway — their diary holds their own
 * lessons, so the only name on it to click is theirs.
 */
export async function getTutor(id: string): Promise<TutorProfile> {
  return get<TutorProfile>(`/admin/tutors/${id}`);
}

/** A school, and how many children here attend it. */
export interface SchoolSummary {
  name: string;
  studentCount: number;
}

/** One child on a school's roster, as the day planner needs them. */
export interface SchoolStudent {
  id: string;
  schoolPupilId?: string;
  fullName: string;
  formClass: string | null;
  instrument: string | null;
  tutorName: string | null;
}

/**
 * The schools children here attend. Owner only.
 *
 * Derived from what is written on the students themselves, so a school appears
 * the moment a child says they go there and disappears when the last one
 * leaves. There is nothing to maintain separately, and nothing to keep in step.
 */
export async function getSchools(): Promise<{ schools: SchoolSummary[] }> {
  return get<{ schools: SchoolSummary[] }>("/admin/schools");
}

/**
 * Every active School-domain pupil at one school. Owner only.
 *
 * Not narrowed to the caller's own students, unlike every other student list —
 * a school lesson that has not been booked yet has no tutor to narrow by. That
 * is why the call is the owner's alone.
 */
export async function getSchoolStudents(
  school: string,
): Promise<{ school: string; students: SchoolStudent[] }> {
  return get<{ school: string; students: SchoolStudent[] }>("/admin/schools/students", { school });
}

/**
 * A school as a payer: who to bill, not who attends. Owner only.
 *
 * `hasAmeliaCustomer` rather than the id itself — this screen states a
 * school pays and edits who to reach at it; whether Amelia already knows
 * them is FastAPI's fact, reported once through the booking-identity
 * link-back, and there is nothing here for a person to do with the number
 * itself.
 */
export interface SchoolPayer {
  id: string;
  name: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string | null;
  hasAmeliaCustomer: boolean;
}

export interface SchoolPayerEditable {
  name: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string | null;
}

/** Every school configured to pay for its pupils' lessons. Owner only. */
export async function getSchoolPayers(): Promise<{ schools: SchoolPayer[] }> {
  return get<{ schools: SchoolPayer[] }>("/admin/school-payers");
}

/** Set a school up as a payer. Owner only. */
export async function createSchoolPayer(fields: SchoolPayerEditable): Promise<SchoolPayer> {
  const result = await send<{ school: SchoolPayer }>("POST", "/admin/school-payers", fields);
  return result.school;
}

/** Correct a school payer's contact details. Owner only. */
export async function updateSchoolPayer(
  id: string,
  changes: Partial<SchoolPayerEditable>,
): Promise<SchoolPayer> {
  const result = await send<{ school: SchoolPayer }>(
    "PATCH",
    `/admin/school-payers/${id}`,
    changes,
  );
  return result.school;
}


export type ConcertStatus = "draft" | "scheduled" | "cancelled" | "completed";

export interface Concert {
  id: string;
  title: string;
  venue: string;
  startsAt: string;
  endsAt: string;
  familyDescription: string | null;
  eligibilityNotes: string | null;
  responseDeadline: string | null;
  capacity: number | null;
  ownerNotes: string | null;
  status: ConcertStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ConcertInput {
  title: string;
  venue: string;
  startsAt: string;
  endsAt: string;
  familyDescription: string | null;
  eligibilityNotes: string | null;
  responseDeadline: string | null;
  capacity: number | null;
  ownerNotes: string | null;
}

export function getConcerts() {
  return get<{ items: Concert[]; limit: number; offset: number }>("/concerts");
}

export function createConcert(input: ConcertInput, idempotencyKey: string) {
  return send<{ event: Concert }>(
    "POST",
    "/concerts",
    input,
    { "Idempotency-Key": idempotencyKey },
  );
}

export function updateConcert(
  id: string,
  input: ConcertInput & { version: number; status: ConcertStatus },
) {
  return send<{ event: Concert }>(
    "PATCH",
    "/concerts/" + encodeURIComponent(id),
    input,
  );
}


export type ConcertInvitationStatus = "invited" | "accepted" | "declined" | "withdrawn";

export interface ConcertInvitationCandidate {
  studentId: string;
  studentName: string;
  instrument: string | null;
  deliverable: boolean;
  invitationStatus: ConcertInvitationStatus | null;
}

export interface ConcertInvitation {
  id: string;
  studentId: string;
  studentName: string;
  instrument: string | null;
  status: ConcertInvitationStatus;
  version: number;
  invitedAt: string;
  respondedAt: string | null;
}

export interface FamilyConcertInvitation {
  invitationId: string;
  studentId: string;
  studentName: string;
  eventId: string;
  eventTitle: string;
  venue: string;
  startsAt: string;
  endsAt: string;
  familyDescription: string | null;
  responseDeadline: string | null;
  eventStatus: ConcertStatus;
  invitationStatus: ConcertInvitationStatus;
  version: number;
  invitedAt: string;
  respondedAt: string | null;
}

export function getConcertInvitationCandidates(eventId: string) {
  return get<{ items: ConcertInvitationCandidate[] }>(
    "/concerts/" + encodeURIComponent(eventId) + "/invitation-candidates",
  );
}

export function getConcertInvitations(eventId: string) {
  return get<{ items: ConcertInvitation[] }>(
    "/concerts/" + encodeURIComponent(eventId) + "/invitations",
  );
}

export function createConcertInvitations(eventId: string, studentIds: string[]) {
  return send<{
    invitedCount: number;
    alreadyInvitedCount: number;
    undeliverableStudentIds: string[];
  }>(
    "POST",
    "/concerts/" + encodeURIComponent(eventId) + "/invitations",
    { studentIds },
  );
}

export function getFamilyConcertInvitations() {
  return get<{ items: FamilyConcertInvitation[] }>("/me/concert-invitations");
}

export function respondToConcertInvitation(
  invitationId: string,
  input: { status: "accepted" | "declined" | "withdrawn"; version: number },
) {
  return send<{
    invitation: { id: string; status: ConcertInvitationStatus; version: number; respondedAt: string | null };
    changed: boolean;
  }>(
    "POST",
    "/me/concert-invitations/" + encodeURIComponent(invitationId) + "/respond",
    input,
  );
}


export interface ConcertProgrammeEntry {
  entryId: string;
  invitationId: string;
  studentId: string;
  studentName: string;
  invitationStatus: ConcertInvitationStatus;
  repertoireId: string | null;
  title: string | null;
  composer: string | null;
  workOrCollection: string | null;
  instrument: string | null;
  readiness: "learning" | "polishing" | "performance_ready" | "completed" | "paused" | null;
  status: "proposed" | "selected";
  runningOrder: number | null;
  performerName: string | null;
  proposalSource: "tutor" | "owner";
  available: boolean;
}

export interface ConcertProgrammeOption {
  invitationId: string;
  studentId: string;
  studentName: string;
  repertoireId: string;
  title: string;
  composer: string | null;
  workOrCollection: string | null;
  instrument: string | null;
  readiness: "learning" | "polishing" | "performance_ready" | "completed" | "paused";
}

export interface ConcertProgramme {
  event: Pick<Concert, "id" | "title" | "venue" | "startsAt" | "endsAt" | "status" | "version">;
  entries: ConcertProgrammeEntry[];
  options: ConcertProgrammeOption[];
  publication: { version: number; token: string; publishedAt: string } | null;
  hasInvalidatedPublication: boolean;
}

export interface TutorConcertProgramme {
  event: { id: string; title: string; startsAt: string };
  learners: Array<{
    invitationId: string;
    studentId: string;
    studentName: string;
    repertoire: Array<{
      id: string;
      title: string;
      composer: string | null;
      workOrCollection: string | null;
      instrument: string | null;
      status: "learning" | "polishing" | "performance_ready" | "completed" | "paused";
      alreadyProposed: boolean;
    }>;
  }>;
}

export interface PublicConcertProgramme {
  event: { title: string; venue: string; startsAt: string; endsAt: string };
  performers: Array<{
    order: number;
    name: string;
    instrument: string | null;
    piece: string;
    composer: string | null;
    workOrCollection: string | null;
  }>;
}

export function getConcertProgramme(eventId: string) {
  return get<ConcertProgramme>("/concerts/" + encodeURIComponent(eventId) + "/programme");
}

export function addConcertProgrammeEntry(
  eventId: string,
  input: { version: number; invitationId: string; repertoireId: string },
) {
  return send<{ created: boolean; version: number }>(
    "POST",
    "/concerts/" + encodeURIComponent(eventId) + "/programme/entries",
    input,
  );
}

export function saveConcertProgramme(
  eventId: string,
  input: { version: number; entries: Array<{ entryId: string; runningOrder: number; performerName: string }> },
) {
  return send<{ changed: boolean; version: number }>(
    "PUT",
    "/concerts/" + encodeURIComponent(eventId) + "/programme",
    input,
  );
}

export function publishConcertProgramme(
  eventId: string,
  input: { version: number; idempotencyKey: string },
) {
  return send<{
    created: boolean;
    current: boolean;
    version: number;
    token: string | null;
    publishedAt: string;
  }>(
    "POST",
    "/concerts/" + encodeURIComponent(eventId) + "/programme/publish",
    input,
  );
}

export function getTutorConcertProgramme(eventId: string) {
  return get<TutorConcertProgramme>(
    "/concerts/" + encodeURIComponent(eventId) + "/my-proposals",
  );
}

export function proposeConcertProgrammePiece(
  eventId: string,
  input: { invitationId: string; repertoireId: string },
) {
  return send<{ created: boolean; version: number }>(
    "POST",
    "/concerts/" + encodeURIComponent(eventId) + "/proposals",
    input,
  );
}

export function getPublicConcertProgramme(token: string) {
  return get<{ programme: PublicConcertProgramme }>(
    "/public/concert-programmes/" + encodeURIComponent(token),
  );
}

